import { Send } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { allowedTargets, chTarget, userTarget } from '@bccomms/shared';
import { session } from '../lib/session';
import { targetLabel, useStore } from '../store';
import { Drawer, fmtTime } from './ui';

export function ChatDrawer({ onClose }: { onClose: () => void }) {
  const me = useStore((s) => s.user);
  const panel = useStore((s) => s.panel);
  const channels = useStore((s) => s.channels);
  const users = useStore((s) => s.users);
  const chat = useStore((s) => s.chat);
  const unread = useStore((s) => s.unread);
  const target = useStore((s) => s.chatTarget);
  const [text, setText] = useState('');
  const logRef = useRef<HTMLDivElement>(null);

  const threads = useMemo(() => {
    const allowed = new Set([...allowedTargets(panel, 'talk'), ...allowedTargets(panel, 'listen')]);
    const chs = channels.filter((c) => allowed.has(chTarget(c.id))).map((c) => chTarget(c.id));
    const ppl = users.filter((u) => u.id !== me?.id).map((u) => userTarget(u.id));
    return [...chs, ...ppl];
  }, [panel, channels, users, me?.id]);

  const active = target ?? threads[0] ?? null;

  const messages = useMemo(() => {
    if (!active) return [];
    if (active.startsWith('user:')) {
      const other = active.slice(5);
      return chat.filter((m) => (m.from === other && m.target === userTarget(me?.id ?? '')) || (m.from === me?.id && m.target === active));
    }
    return chat.filter((m) => m.target === active);
  }, [chat, active, me?.id]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [messages.length, active]);

  const send = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setText('');
    await session.sendChat(active, t);
  };

  return (
    <Drawer title="Messages" onClose={onClose}>
      <div className="chat">
        <div className="chat__threads">
          {threads.map((t) => (
            <button key={t} className={`thread ${t === active ? 'thread--on' : ''}`} onClick={() => session.openChat(t)}>
              {t.startsWith('ch:') ? '#' : '@'} {targetLabel(t)}
              {unread[t] ? <span className="thread__n">{unread[t]}</span> : null}
            </button>
          ))}
        </div>
        <div className="chat__log" ref={logRef}>
          {messages.length === 0 && <p className="muted" style={{ textAlign: 'center', marginTop: 32 }}>No messages yet.</p>}
          {messages.map((m) => (
            <div key={m.id} className={`msg ${m.from === me?.id ? 'msg--me' : ''}`}>
              <div className="msg__meta">
                <span>{m.from === me?.id ? 'you' : (users.find((u) => u.id === m.from)?.displayName ?? '?')}</span>
                <span>{fmtTime(m.ts)}</span>
              </div>
              {m.text}
            </div>
          ))}
        </div>
        <div className="chat__compose">
          <input
            className="input"
            placeholder={active ? `Message ${targetLabel(active)}` : 'No thread'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && void send()}
            disabled={!active}
          />
          <button className="btn btn--primary" onClick={() => void send()} disabled={!text.trim() || !active} aria-label="Send">
            <Send size={16} />
          </button>
        </div>
      </div>
    </Drawer>
  );
}
